import { DEFAULT_PAGE_SIZE } from '../config';
import {
  BaconOperators,
  BaconCommandKeys,
  BaconEngine,
  BaconQueryPart,
  BaconFilterQueryPart,
  BaconPivotQueryPart,
  BaconHideValueQueryPart,
  BaconValueQueryPart,
  BaconAxisQueryPart,
  isBaconButtonWidget,
  isBaconDateRangeWidget,
  BaconColumnType,
} from './bacon';

// #region Types
export enum JerkyCommandTypes {
  FILTER = 'filter',
  AXIS = 'axis',
  VALUE = 'value',
  HIDE_VALUE = 'hideValue',
  PIVOT = 'pivot',
  ORDER = 'order',
}

export interface BaseJerkyCommand {
  type: JerkyCommandTypes;
  column: string;
}

export interface JerkyFilterCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.FILTER;
  operator: BaconOperators;
  value: string | string[];
}

export interface JerkyAxisCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.AXIS;
}

export interface JerkyValueCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.VALUE;
}

export interface JerkyHideValueCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.HIDE_VALUE;
}

export interface JerkyPivotCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.PIVOT;
}

export interface JerkyOrderCommand extends BaseJerkyCommand {
  type: JerkyCommandTypes.ORDER;
}

export type JerkyCommand =
  | JerkyFilterCommand
  | JerkyAxisCommand
  | JerkyValueCommand
  | JerkyHideValueCommand
  | JerkyPivotCommand
  | JerkyOrderCommand;

export interface JerkyPagination {
  limit: number;
  offset: number;
  count: number;
}

export const isJerkyFilterCommand = (
  command: JerkyCommand
): command is JerkyFilterCommand => command.type === JerkyCommandTypes.FILTER;
export const isJerkyAxisCommand = (
  command: JerkyCommand
): command is JerkyAxisCommand => command.type === JerkyCommandTypes.AXIS;
export const isJerkyValueCommand = (
  command: JerkyCommand
): command is JerkyValueCommand => command.type === JerkyCommandTypes.VALUE;
export const isJerkyHideValueCommand = (
  command: JerkyCommand
): command is JerkyHideValueCommand =>
  command.type === JerkyCommandTypes.HIDE_VALUE;
export const isJerkyPivotCommand = (
  command: JerkyCommand
): command is JerkyPivotCommand => command.type === JerkyCommandTypes.PIVOT;
export const isJerkyOrderCommand = (
  command: JerkyCommand
): command is JerkyOrderCommand => command.type === JerkyCommandTypes.ORDER;

export const BACON_KEY_TO_JERKY_TYPE_MAP = {
  [BaconCommandKeys.FILTER]: JerkyCommandTypes.FILTER,
  [BaconCommandKeys.VALUE]: JerkyCommandTypes.VALUE,
  [BaconCommandKeys.HIDE_VALUE]: JerkyCommandTypes.HIDE_VALUE,
  [BaconCommandKeys.PIVOT]: JerkyCommandTypes.PIVOT,
  [BaconCommandKeys.ORDER]: JerkyCommandTypes.ORDER,
};

export const JERKY_TYPE_TO_BACON_KEY_MAP = {
  [JerkyCommandTypes.FILTER]: BaconCommandKeys.FILTER,
  [JerkyCommandTypes.VALUE]: BaconCommandKeys.VALUE,
  [JerkyCommandTypes.HIDE_VALUE]: BaconCommandKeys.HIDE_VALUE,
  [JerkyCommandTypes.PIVOT]: BaconCommandKeys.PIVOT,
  [JerkyCommandTypes.ORDER]: BaconCommandKeys.ORDER,
};

export interface JerkyNavOption {
  label: string;
  isSelected: boolean;
  baconPart?: BaconQueryPart;
}

export interface JerkyAxis extends JerkyNavOption {}

export interface JerkyDimension {
  label: string;
  axes: { [label: string]: JerkyAxis };
}

export interface JerkyDimensions {
  [label: string]: JerkyDimension;
}

export interface JerkyValue extends JerkyNavOption {}

export interface JerkyValues {
  [label: string]: JerkyValue;
}

export interface JerkyHideValue extends JerkyNavOption {}

export interface JerkyHideValues {
  [label: string]: JerkyHideValue;
}

export interface JerkyActiveFilter extends JerkyNavOption {}

export interface JerkyActiveFilters {
  [label: string]: JerkyActiveFilter;
}

export interface BaseJerkyDynamicFilter {
  label: string;
}

export interface JerkyDynamicDateFilter extends BaseJerkyDynamicFilter {
  type: 'date';
  value: { from?: string; to?: string };
  fromPart?: BaconFilterQueryPart;
  toPart?: BaconFilterQueryPart;
}

export type JerkyDynamicFilter = JerkyDynamicDateFilter;

export interface JerkyDynamicFilters {
  [label: string]: JerkyDynamicFilter;
}

export const isJerkyDynamicDateFilter = (
  filter: JerkyDynamicFilter
): filter is JerkyDynamicDateFilter => filter.type === 'date';

export interface JerkyDownload {
  name: string;
  label: string;
  url: string;
}

export interface JerkyDownloads {
  excel?: JerkyDownload;
  csv?: JerkyDownload;
}

export interface JerkyPivotValue {
  label?: string;
  isHidden?: boolean;
  isFiltered?: boolean;
  baconPart?: BaconQueryPart;
}

// pivot: value
export interface JerkyPivotValueMap {
  [pivot: string]: string;
}

export interface JerkyPivotValues {
  [label: string]: JerkyPivotValue;
}

export interface JerkyPivot extends JerkyNavOption {
  values: JerkyPivotValues;
  valueOrder: JerkyPivotValue['label'][];
}

export interface JerkyPivots {
  [label: string]: JerkyPivot;
}

export interface JerkyColumn {
  label: string;
  type: BaconColumnType;
  total?: string;
  drop?: string;
  pivot?: string;
  resetOrder?: string;
  orderDesc?: string;
  orderAsc?: string;
  pivots?: JerkyPivotValueMap;
}

export interface JerkyCell {
  value: string;
  filter?: string;
  action?: string;
}

export type JerkyRow = JerkyCell[];

export interface JerkyData {
  dimensions: JerkyDimensions;
  values: JerkyValues;
  hideValues: JerkyHideValues;
  activeFilters: JerkyActiveFilters;
  dynamicFilters: JerkyDynamicFilters;
  downloads: JerkyDownloads;
  pivots: JerkyPivots;
  pivotOrder: JerkyPivot['label'][];
  columns: JerkyColumn[];
  rows: JerkyRow[];
}

export const DEFAULT_JERKY_DATA: JerkyData = {
  dimensions: {},
  values: {},
  hideValues: {},
  activeFilters: {},
  dynamicFilters: {},
  downloads: {},
  pivots: {},
  pivotOrder: [],
  columns: [],
  rows: [],
};

export interface ColumnNameMap {
  [label: string]: string;
}
// #endregion

// #region Utility Functions
// #endregion

// #region Main Class
export class JerkyEngine {
  bacon: BaconEngine;

  data: JerkyData = DEFAULT_JERKY_DATA;
  localData: JerkyData = DEFAULT_JERKY_DATA;
  pagination: JerkyPagination;

  // For manually setting what label maps to what column
  columnNameMap: ColumnNameMap;

  // Can be watched to trigger state updates;
  hash: number = 0;
  isLoading: boolean = false;
  error: React.ReactNode = undefined;
  onChange: (hash: number) => void;
  navHasChanged: boolean = true;

  constructor(
    endpoint: string,
    onChange: (hash: number) => void,
    columnNameMap: ColumnNameMap = {},
    baseUrl: string = window.urls.reports,
    defaultPagination: JerkyPagination = {
      limit: DEFAULT_PAGE_SIZE,
      offset: 0,
      count: 0,
    }
  ) {
    this.bacon = new BaconEngine(endpoint, baseUrl);
    this.onChange = onChange;
    this.columnNameMap = columnNameMap;
    this.pagination = defaultPagination;
  }

  // Sets the query directly in the bacon engine
  async setRawQuery(query: string, pagination: string = '') {
    this.isLoading = true;
    this.handleChange();
    this.bacon.setQuery(query);
    this.bacon.setPagination(pagination);

    try {
      await this.bacon.fetch();
      const params = new URLSearchParams(window.location.search);
      params.set('q', this.bacon.query);
      params.set('t', this.bacon.pagination);
      window.history.pushState(
        {},
        document.title,
        `${window.location.pathname}?${params}`
          .replaceAll('%3A', ':')
          .replaceAll('%2F', '/')
      );
      this.parseBaconData();
    } catch (error) {
      window &&
        window?.deployment?.environment !== 'production' &&
        console.error(error);
      this.error = error.message;
    }
    this.isLoading = false;
    this.handleChange();
  }

  async fetch() {
    await this.setRawQuery(
      this.serialiseLocalData(),
      this.serialisePagination()
    );
  }

  handleChange() {
    this.hash += 1;
    this.onChange?.(this.hash);
  }

  handleNavChange() {
    this.navHasChanged = true;
    this.handleChange();
  }

  setAxis(dimension: string, axis: string, isSelected: boolean) {
    if (
      this.localData.dimensions[dimension] &&
      this.localData.dimensions[dimension].axes[axis] &&
      // We have no way of turning off already selected axes
      !this.data.dimensions[dimension].axes[axis].isSelected
    ) {
      this.localData.dimensions[dimension].axes[axis].isSelected = isSelected;
      this.handleNavChange();
    }
  }

  toggleAxis(dimension: string, axis: string) {
    if (
      this.localData.dimensions[dimension] &&
      this.localData.dimensions[dimension].axes[axis] &&
      // We have no way of turning off already selected axes
      !this.data.dimensions[dimension].axes[axis].isSelected
    ) {
      this.localData.dimensions[dimension].axes[axis].isSelected =
        !this.localData.dimensions[dimension].axes[axis].isSelected;
      this.handleNavChange();
    }
  }

  setValue(value: string, isSelected: boolean) {
    if (this.localData.values[value]) {
      this.localData.values[value].isSelected = isSelected;
      this.handleNavChange();
    }
  }

  toggleValue(value: string) {
    if (this.localData.values[value]) {
      this.localData.values[value].isSelected =
        !this.localData.values[value].isSelected;
      this.handleNavChange();
    }
  }

  setHideValue(hideValue: string, isSelected: boolean) {
    if (this.localData.hideValues[hideValue]) {
      this.localData.hideValues[hideValue].isSelected = isSelected;
      this.handleNavChange();
    }
  }

  toggleHideValue(hideValue: string) {
    if (this.localData.hideValues[hideValue]) {
      this.localData.hideValues[hideValue].isSelected =
        !this.localData.hideValues[hideValue].isSelected;
      this.handleNavChange();
    }
  }

  setActiveFilter(activeFilter: string, isSelected: boolean) {
    if (this.localData.activeFilters[activeFilter]) {
      this.localData.activeFilters[activeFilter].isSelected = isSelected;
      this.handleNavChange();
    }
  }

  toggleActiveFilter(activeFilter: string) {
    if (this.localData.activeFilters[activeFilter]) {
      this.localData.activeFilters[activeFilter].isSelected =
        !this.localData.activeFilters[activeFilter].isSelected;
      this.handleNavChange();
    }
  }

  setPivot(pivot: string, isSelected: boolean) {
    if (this.localData.pivots[pivot]) {
      this.localData.pivots[pivot].isSelected = isSelected;
      this.handleNavChange();
    }
  }

  togglePivot(pivot: string) {
    if (this.localData.pivots[pivot]) {
      this.localData.pivots[pivot].isSelected =
        !this.localData.pivots[pivot].isSelected;
      this.handleNavChange();
    }
  }

  setPivotFilterValue(pivot: string, value: string, isFiltered: boolean) {
    if (
      this.localData.pivots[pivot] &&
      this.localData.pivots[pivot].values[value]
    ) {
      this.localData.pivots[pivot].values[value].isFiltered = isFiltered;
      if (isFiltered)
        this.localData.pivots[pivot].values[value].isHidden = false;
      this.handleNavChange();
    }
  }

  togglePivotFilterValue(pivot: string, value: string) {
    if (
      this.localData.pivots[pivot] &&
      this.localData.pivots[pivot].values[value]
    ) {
      this.localData.pivots[pivot].values[value].isFiltered =
        !this.localData.pivots[pivot].values[value].isFiltered;
      if (this.localData.pivots[pivot].values[value].isFiltered)
        this.localData.pivots[pivot].values[value].isHidden = false;
      this.handleNavChange();
    }
  }

  setPivotHideValue(pivot: string, value: string, isHidden: boolean) {
    if (
      this.localData.pivots[pivot] &&
      this.localData.pivots[pivot].values[value]
    ) {
      this.localData.pivots[pivot].values[value].isHidden = isHidden;
      if (isHidden)
        this.localData.pivots[pivot].values[value].isFiltered = false;
      this.handleNavChange();
    }
  }

  togglePivotHideValue(pivot: string, value: string) {
    if (
      this.localData.pivots[pivot] &&
      this.localData.pivots[pivot].values[value]
    ) {
      this.localData.pivots[pivot].values[value].isHidden =
        !this.localData.pivots[pivot].values[value].isHidden;
      if (this.localData.pivots[pivot].values[value].isHidden)
        this.localData.pivots[pivot].values[value].isFiltered = false;
      this.handleNavChange();
    }
  }

  setDateFilter(dateFilter: string, value?: { from?: string; to?: string }) {
    if (
      this.localData.dynamicFilters[dateFilter] &&
      isJerkyDynamicDateFilter(this.localData.dynamicFilters[dateFilter])
    ) {
      this.localData.dynamicFilters[dateFilter].value = value;
      this.handleNavChange();
    }
  }

  serialisePagination() {
    return `${this.pagination.limit}:${
      this.pagination.limit * this.pagination.offset
    }:${this.pagination.count}`;
  }

  serialiseLocalData() {
    let query = '';

    const serialisedActiveFilters = this.serialiseActiveFilters();
    const serialisedAxes = this.serialiseAxes();
    const serialisedValues = this.serialiseValues();
    const serialisedHideValues = this.serialiseHideValues();
    const serialisedPivots = this.serialisePivots();
    const serialisedDynamicFilters = this.serialiseDynamicFilters();
    const serialisedOrder = this.bacon.serialiseQueryParts(
      this.bacon
        .getQueryParts()
        .filter((part) => part.command === BaconCommandKeys.ORDER)
    );

    if (serialisedActiveFilters) query += `/${serialisedActiveFilters}`;
    if (serialisedAxes) query += `/${serialisedAxes}`;
    if (serialisedValues) query += `/${serialisedValues}`;
    if (serialisedHideValues) query += `/${serialisedHideValues}`;
    if (serialisedPivots) query += `/${serialisedPivots}`;
    if (serialisedDynamicFilters) query += `/${serialisedDynamicFilters}`;
    if (serialisedOrder) query += `/${serialisedOrder}`;

    return query.replace('/', '');
  }

  serialiseActiveFilters() {
    const activeFilterParts: BaconQueryPart[] = [];
    const dynamicFilterColumns: string[] = [];

    Object.values(this.localData.dynamicFilters).forEach((filter) => {
      if (isJerkyDynamicDateFilter(filter)) {
        dynamicFilterColumns.push(filter.fromPart.column);
      }
    });

    Object.values(this.localData.activeFilters).forEach((filter) => {
      if (
        filter.isSelected &&
        filter.baconPart &&
        !dynamicFilterColumns.includes(filter.baconPart.column)
      )
        activeFilterParts.push(filter.baconPart);
    });

    return this.bacon.serialiseQueryParts(activeFilterParts);
  }

  serialiseAxes() {
    const axesParts: BaconQueryPart[] = [];

    Object.values(this.localData.dimensions).forEach((dimension) =>
      Object.values(dimension.axes).forEach((axis) => {
        if (axis.isSelected && axis.baconPart) axesParts.push(axis.baconPart);
      })
    );

    let existingAxes = this.bacon.query
      .split('/')
      .filter((part) => part.startsWith(BaconCommandKeys.AXIS))
      .join('/');

    if (existingAxes && axesParts.length) existingAxes += '/';

    return existingAxes + this.bacon.serialiseQueryParts(axesParts);
  }

  serialiseValues() {
    let valuesParts: BaconQueryPart[] = this.bacon
      .getQueryParts(this.bacon.query)
      .filter((part) =>
        [BaconCommandKeys.VALUE, BaconCommandKeys.HIDE_VALUE].includes(
          part.command
        )
      );
    Object.values(this.localData.values).forEach((value) => {
      if (
        value.isSelected &&
        value.baconPart?.command === BaconCommandKeys.VALUE
      )
        valuesParts.push(value.baconPart);
      if (
        value.isSelected &&
        value.baconPart?.command === BaconCommandKeys.HIDE_VALUE
      )
        valuesParts = valuesParts.filter(
          (part) => part.column !== value.baconPart.column
        );
    });

    return this.bacon.serialiseQueryParts(valuesParts);
  }

  serialiseHideValues() {
    const hideValuesParts: BaconQueryPart[] = [];
    Object.values(this.localData.hideValues).forEach((hideValue) => {
      if (!hideValue.isSelected && hideValue.baconPart)
        hideValuesParts.push(hideValue.baconPart);
    });

    return this.bacon.serialiseQueryParts(hideValuesParts);
  }

  serialisePivots() {
    const pivotParts: BaconQueryPart[] = [];

    Object.values(this.localData.pivots).forEach((pivot) => {
      if (pivot.isSelected && pivot.baconPart) {
        pivotParts.push(pivot.baconPart);
        Object.values(pivot.values).forEach((value) => {
          if (value.isFiltered) {
            pivotParts.push({
              ...value.baconPart,
              operator: BaconOperators.EQUALS,
            } as BaconFilterQueryPart);
          } else if (value.isHidden) {
            pivotParts.push({
              ...value.baconPart,
              operator: BaconOperators.NOT_EQUALS,
            } as BaconFilterQueryPart);
          }
        });
      }
    });

    return this.bacon.serialiseQueryParts(pivotParts);
  }

  serialiseDynamicFilters() {
    const filterParts: BaconQueryPart[] = [];

    Object.values(this.localData.dynamicFilters).forEach((filter) => {
      if (isJerkyDynamicDateFilter(filter)) {
        if (filter.value.to) {
          filterParts.push({
            ...filter.toPart,
            value: filter.value.to,
          });
        }
        if (filter.value.from) {
          filterParts.push({
            ...filter.fromPart,
            value: filter.value.from,
          });
        }
      }
    });

    return this.bacon.serialiseQueryParts(filterParts);
  }

  parseBaconData() {
    let data: JerkyData = DEFAULT_JERKY_DATA;

    data.dimensions = this.parseDimensions();
    data.values = this.parseValues();
    data.hideValues = this.parseHideValues();
    data.activeFilters = this.parseFilters();
    data = { ...data, ...this.parsePivots() };
    data = { ...data, ...this.parseWidgets() };
    data.columns = this.parseColumns(data.pivots, data.pivotOrder);
    data.rows = this.parseRows(data.columns);

    this.data = JSON.parse(JSON.stringify(data));
    this.localData = JSON.parse(JSON.stringify(data));
    this.pagination = JSON.parse(JSON.stringify(this.parsePagination()));
  }

  parsePagination() {
    const pagination = { ...this.pagination };

    const currentPage = this.bacon.table?.pages?.find((page) => page.current);
    pagination.offset = parseInt(currentPage?.label || '1') - 1;

    const pageWithUrl = this.bacon.table?.pages?.find((page) =>
      this.bacon.table.links[page?.url]?.match(/t=[\d]+:[\d]+:[\d]+/)
    );

    if (pageWithUrl && pageWithUrl.url) {
      if (pageWithUrl.url) {
        const t = new URLSearchParams(this.bacon.table.links[pageWithUrl.url])
          .get('t')
          .split(':');
        pagination.limit = parseInt(t[0]);
        pagination.count = parseInt(t[2] || '0');
      }
    }

    return pagination;
  }

  parseDimensions() {
    const data: JerkyDimensions = {};

    this.bacon.nav.axes?.forEach((dimension) => {
      data[dimension.dimension] = {
        label: dimension.dimension,
        axes: {},
      };
      dimension.axes.forEach((axis) => {
        data[dimension.dimension].axes[axis.label] = {
          label: axis.label,
          isSelected: !axis.url,
          baconPart: (this.columnNameMap[axis.label]
            ? {
                command: BaconCommandKeys.AXIS,
                column: this.columnNameMap[axis.label],
              }
            : this.bacon.getChangedPart(
                this.bacon.cleanQueryUrl(axis.url),
                this.bacon.query,
                BaconCommandKeys.AXIS
              )) as BaconAxisQueryPart,
        };
      });
    });

    this.navHasChanged = false;

    return data;
  }

  parseValues() {
    const data: JerkyValues = {};

    this.bacon.nav.values?.forEach((value) => {
      data[value.label] = {
        label: value.label,
        isSelected: false,
        baconPart: (this.columnNameMap[value.label]
          ? {
              command: BaconCommandKeys.VALUE,
              column: this.columnNameMap[value.label],
            }
          : this.bacon.getChangedPart(
              this.bacon.cleanQueryUrl(value.show_url),
              this.bacon.query
            )) as BaconValueQueryPart,
      };
    });

    return data;
  }

  parseHideValues() {
    const data: JerkyHideValues = {};

    this.bacon.nav.hideValues?.forEach((hideValue) => {
      data[hideValue.label] = {
        label: hideValue.label,
        isSelected: false,
        baconPart: (this.columnNameMap[hideValue.label]
          ? {
              command: BaconCommandKeys.HIDE_VALUE,
              column: this.columnNameMap[hideValue.label],
            }
          : this.bacon.getChangedPart(
              this.bacon.cleanQueryUrl(hideValue.hide_url),
              this.bacon.query,
              BaconCommandKeys.HIDE_VALUE
            )) as BaconHideValueQueryPart,
      };
    });

    return data;
  }

  parseFilters() {
    const data: JerkyActiveFilters = {};
    this.bacon.nav.filters?.forEach((filter) => {
      data[filter.label] = {
        label: filter.label,
        isSelected: true,
        baconPart: this.bacon.getChangedPart(
          this.bacon.cleanQueryUrl(filter.drop_url),
          this.bacon.query,
          BaconCommandKeys.FILTER
        ),
      };
    });

    return data;
  }

  parseWidgets() {
    const downloads: JerkyDownloads = {};
    const dynamicFilters: JerkyDynamicFilters = {};

    this.bacon.nav.widgets?.forEach((widget) => {
      if (
        isBaconButtonWidget(widget) &&
        (widget.label === 'Downloads' || widget.label === 'Download')
      ) {
        widget.buttons.forEach((button) => {
          if (button.name === 'csv') downloads.csv = button;
          if (button.name === 'excel') downloads.excel = button;
        });
      }

      if (isBaconDateRangeWidget(widget)) {
        const partBase = this.bacon.getChangedPart(
          this.bacon.cleanQueryUrl(widget.urls.both_values),
          this.bacon.query,
          BaconCommandKeys.FILTER
        );
        dynamicFilters[widget.label] = {
          type: 'date',
          label: widget.label,
          value: widget.values,
          fromPart: {
            command: BaconCommandKeys.FILTER,
            column: this.columnNameMap[widget.label] || partBase?.column,
            operator: BaconOperators.GREATER_THAN_OR_EQUAL,
            value: widget.values.from,
          },
          toPart: {
            command: BaconCommandKeys.FILTER,
            column: this.columnNameMap[widget.label] || partBase?.column,
            operator: BaconOperators.LESS_THAN_OR_EQUAL,
            value: widget.values.to,
          },
        };
      }
    });

    return { downloads, dynamicFilters };
  }

  parsePivots() {
    const pivots: JerkyPivots = {};
    const pivotOrder: JerkyPivot['label'][] = [];

    this.bacon.table.pivots?.forEach((pivot) => {
      const pivotBaconPart = this.columnNameMap[pivot.label]
        ? {
            command: BaconCommandKeys.PIVOT,
            column: this.columnNameMap[pivot.label],
          }
        : this.bacon.getChangedPart(
            this.bacon.cleanQueryUrl(
              this.bacon.table.links[pivot.links.drop_axis]
            ),
            this.bacon.query,
            BaconCommandKeys.PIVOT
          );

      pivotOrder.push(pivot.label);
      pivots[pivot.label] = {
        label: pivot.label,
        isSelected: true,
        baconPart: pivotBaconPart as BaconPivotQueryPart,
        valueOrder: pivot.values.map((value) => value.label),
        values: pivot.values.reduce((acc, value) => {
          acc[value.label] = {
            label: value.label,
            isHidden: false,
            isFiltered: false,
            baconPart: this.bacon.getChangedPart(
              this.bacon.cleanQueryUrl(
                this.bacon.table.links[value.links.filter]
              ),
              this.bacon.query,
              BaconCommandKeys.FILTER
            ),
          };
          return acc;
        }, {}),
      };
    });

    return { pivots, pivotOrder };
  }

  parseColumns(pivots?: JerkyPivots, pivotOrder?: JerkyPivot['label'][]) {
    const columns: JerkyColumn[] = [];
    // pivot: value
    let pivotColumns: JerkyPivotValueMap[] = [];

    if (pivots && pivotColumns) {
      for (let i = 0; i < pivotOrder.length; i += 1) {
        const pivot = pivotOrder[i];
        const pivotValues = Object.keys(pivots[pivot].values);
        const _pivotColumns: JerkyPivotValueMap[] = [];
        if (i === 0) {
          pivotValues.forEach((value) =>
            _pivotColumns.push({ [pivot]: value })
          );
        } else {
          pivotColumns.forEach((pivotValueMap) =>
            pivotValues.forEach((value) =>
              _pivotColumns.push({ ...pivotValueMap, [pivot]: value })
            )
          );
        }
        pivotColumns = _pivotColumns;
      }
    }

    let firstValueColumn = null;
    let valueIndex = 0;
    this.bacon.table.columns.forEach((column, index) => {
      if (column.type === BaconColumnType.VALUE) {
        if (firstValueColumn == null) {
          firstValueColumn = column.label;
        } else if (firstValueColumn === column.label) {
          valueIndex += 1;
        }
      }

      columns.push({
        label: column.label,
        type: column.type,
        total: column.total,
        drop: this.bacon.cleanQueryUrl(
          this.bacon.table.links[column.links?.drop_axis] ||
            this.bacon.table.links[column.links?.hide]
        ),
        pivot: this.bacon.cleanQueryUrl(
          this.bacon.table.links[column.links?.pivot]
        ),
        resetOrder: this.bacon.cleanQueryUrl(
          this.bacon.table.links[column.links?.reset_order]
        ),
        orderDesc: this.bacon.cleanQueryUrl(
          this.bacon.table.links[column.links?.order]
        ),
        orderAsc: this.bacon.cleanQueryUrl(
          this.bacon.table.links[column.links?.order_asc]
        ),
        pivots: pivotColumns[valueIndex],
      });
    });

    return columns;
  }

  parseRows(columns: JerkyColumn[]) {
    const rows: JerkyRow[] = [];

    this.bacon.table.rows.forEach((row) => {
      const _row = [];

      row.forEach((row, index) => {
        if (typeof row === 'object') {
          _row.push({
            value: row[0],
            filter: row[1]?.filter
              ? this.bacon.cleanQueryUrl(this.bacon.table.links[row[1].filter])
              : undefined,
            action:
              columns[index].type === BaconColumnType.ACTION
                ? this.bacon.cleanQueryUrl(row.toString())
                : undefined,
          });
        } else {
          _row.push({
            value: row,
            action:
              columns[index].type === BaconColumnType.ACTION
                ? this.bacon.cleanQueryUrl(row.toString())
                : undefined,
          });
        }
      });

      rows.push(_row);
    });

    return rows;
  }
}
// #endregion
